import { useEffect, useMemo, useRef, useState } from "react";
import {
  AlertTriangle,
  Camera,
  CheckCircle2,
  Download,
  FileSpreadsheet,
  RefreshCw,
  ShieldCheck,
  Upload,
} from "lucide-react";
import { Link } from "react-router-dom";
import {
  Card,
  InlineNotice,
  Money,
  PageHeader,
  StatusBadge,
} from "../components/ui";
import { api } from "../lib/api";
import { downloadText, parseCsv, toCsv } from "../lib/csv";
import { refreshFromServer } from "../lib/data";

type PaymentMethod = "CASH" | "BANK_TRANSFER" | "DEBT";
type SourceType = "CSV" | "XLSX" | "OCR";
type RawRow = Record<string, string | number | boolean | null>;

type Mapping = {
  sku?: string;
  barcode?: string;
  productName?: string;
  unit?: string;
  quantity: string;
  unitCost: string;
  lotCode?: string;
  expiryDate?: string;
};

type Supplier = {
  id: string;
  name: string;
  phone?: string | null;
};

type NormalizedLine = {
  productId: string;
  productName: string;
  sku: string;
  barcode: string | null;
  unitId: string | null;
  unitName: string;
  quantity: number;
  unitCost: number;
  lotCode: string | null;
  expiryDate: string | null;
  matchMethod: "SKU" | "BARCODE" | "NAME";
};

type DraftLine = {
  rowNumber: number;
  rawJson?: RawRow;
  raw?: RawRow;
  normalizedJson?: NormalizedLine | null;
  normalized?: NormalizedLine | null;
  status: "VALID" | "WARNING" | "ERROR" | string;
  errorJson?: {
    errors?: string[];
    warnings?: string[];
  } | null;
  errors?: string[];
  warnings?: string[];
};

type ImportSummary = {
  total: number;
  valid: number;
  warnings: number;
  errors: number;
  metadataErrors: string[];
};

type PreviewResponse = {
  job: {
    id: string;
    status: string;
    lines: DraftLine[];
  };
  summary: ImportSummary;
};

type RevalidateResponse = {
  jobId: string;
  status: string;
  summary: ImportSummary;
  lines: DraftLine[];
};

type OcrCapabilities = {
  enabled: boolean;
  mode: string;
  acceptedMimeTypes: string[];
  maxImageBytes: number;
  message: string;
};

const RECEIPT_TEMPLATE_HEADERS = [
  "SKU",
  "Mã vạch",
  "Tên sản phẩm",
  "Đơn vị",
  "Số lượng",
  "Giá nhập",
  "Mã lô",
  "Hạn sử dụng",
];

const FIELD_LABELS: Array<{
  key: keyof Mapping;
  label: string;
  required?: boolean;
}> = [
  { key: "sku", label: "SKU / Mã hàng", required: true },
  { key: "barcode", label: "Mã vạch" },
  { key: "productName", label: "Tên sản phẩm" },
  { key: "unit", label: "Đơn vị / Quy cách" },
  { key: "quantity", label: "Số lượng", required: true },
  { key: "unitCost", label: "Giá nhập / đơn vị", required: true },
  { key: "lotCode", label: "Mã lô" },
  { key: "expiryDate", label: "Hạn sử dụng (YYYY-MM-DD)" },
];

const ALIASES: Record<keyof Mapping, string[]> = {
  sku: [
    "sku",
    "ma sku",
    "mã sku",
    "ma hang",
    "mã hàng",
    "ma sp",
    "mã sp",
    "ma san pham",
    "mã sản phẩm",
    "item code",
    "product code",
  ],
  barcode: ["barcode", "ma vach", "mã vạch", "ean", "upc"],
  productName: [
    "name",
    "product",
    "product name",
    "ten hang",
    "tên hàng",
    "ten san pham",
    "tên sản phẩm",
  ],
  unit: ["unit", "don vi", "đơn vị", "dvt", "đvt", "quy cach", "quy cách"],
  quantity: ["quantity", "qty", "so luong", "số lượng", "sl"],
  unitCost: [
    "unitcost",
    "unit cost",
    "gia nhap",
    "giá nhập",
    "don gia",
    "đơn giá",
    "cost",
  ],
  lotCode: [
    "lot",
    "lotcode",
    "lot code",
    "ma lo",
    "mã lô",
    "so lo",
    "số lô",
    "lo",
    "lô",
    "batch",
    "so batch",
    "số batch",
    "batch number",
    "batch no",
  ],
  expiryDate: [
    "expirydate",
    "expiry date",
    "expiry",
    "exp date",
    "exp",
    "best before",
    "hsd",
    "han su dung",
    "hạn sử dụng",
    "han dung",
    "hạn dùng",
  ],
};

function fold(value: string): string {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/đ/gi, "d")
    .trim()
    .toLocaleLowerCase("vi")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ");
}

function autoMapping(headers: string[]): Mapping {
  const normalized = headers.map((header) => ({ header, folded: fold(header) }));

  function find(key: keyof Mapping): string | undefined {
    const aliases = ALIASES[key].map(fold);
    return normalized.find((item) => aliases.includes(item.folded))?.header;
  }

  return {
    sku: find("sku"),
    barcode: find("barcode"),
    productName: find("productName"),
    unit: find("unit"),
    quantity: find("quantity") ?? "",
    unitCost: find("unitCost") ?? "",
    lotCode: find("lotCode"),
    expiryDate: find("expiryDate"),
  };
}

function looksLikeProductCatalog(headers: string[], fileName: string): boolean {
  const normalized = new Set(headers.map(fold));
  const foldedFileName = fold(fileName);
  const productSignals = [
    "saleprice",
    "gia ban",
    "giá bán",
    "minstockqty",
    "ton toi thieu",
    "tồn tối thiểu",
    "trackexpiry",
    "theo doi hsd",
    "theo dõi hsd",
    "unitsjson",
    "quy cach quy doi",
    "quy cách quy đổi",
  ].map(fold);

  const hasProductSignal = productSignals.some((signal) =>
    normalized.has(signal),
  );
  const mapping = autoMapping(headers);
  const missingReceiptFacts = !mapping.quantity || !mapping.unitCost;

  return (
    foldedFileName.includes("danh muc san pham") ||
    foldedFileName.includes("danh muc sku") ||
    (Boolean(mapping.sku) && hasProductSignal && missingReceiptFacts)
  );
}

async function fileHash(file: File): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", await file.arrayBuffer());
  return [...new Uint8Array(digest)]
    .map((value) => value.toString(16).padStart(2, "0"))
    .join("");
}

function toBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  const chunk = 0x8000;
  let binary = "";
  for (let offset = 0; offset < bytes.length; offset += chunk) {
    binary += String.fromCharCode(...bytes.subarray(offset, offset + chunk));
  }
  return btoa(binary);
}

function draftRaw(line: DraftLine): RawRow {
  return line.rawJson ?? line.raw ?? {};
}

function draftNormalized(line: DraftLine): NormalizedLine | null {
  return line.normalizedJson ?? line.normalized ?? null;
}

function draftErrors(line: DraftLine): string[] {
  return line.errorJson?.errors ?? line.errors ?? [];
}

function draftWarnings(line: DraftLine): string[] {
  return line.errorJson?.warnings ?? line.warnings ?? [];
}

function paymentLabel(value: PaymentMethod): string {
  if (value === "CASH") return "Tiền mặt";
  if (value === "BANK_TRANSFER") return "Chuyển khoản";
  return "Ghi nợ nhà cung cấp";
}

export function SmartImportPage() {
  const fileRef = useRef<HTMLInputElement | null>(null);
  const imageRef = useRef<HTMLInputElement | null>(null);

  const [sourceType, setSourceType] = useState<SourceType | null>(null);
  const [fileName, setFileName] = useState("");
  const [sourceHash, setSourceHash] = useState("");
  const [headers, setHeaders] = useState<string[]>([]);
  const [rows, setRows] = useState<RawRow[]>([]);
  const [mapping, setMapping] = useState<Mapping>({ quantity: "", unitCost: "" });
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [supplierId, setSupplierId] = useState("");
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>("CASH");
  const [note, setNote] = useState("");
  const [jobId, setJobId] = useState("");
  const [summary, setSummary] = useState<ImportSummary | null>(null);
  const [draftLines, setDraftLines] = useState<DraftLine[]>([]);
  const [confirmedReceiptId, setConfirmedReceiptId] = useState("");
  const [ocr, setOcr] = useState<OcrCapabilities | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    void Promise.all([
      api<Supplier[]>("/suppliers"),
      api<OcrCapabilities>("/imports/ocr/capabilities"),
    ])
      .then(([supplierRows, capabilities]) => {
        setSuppliers(supplierRows);
        setOcr(capabilities);
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : "Không tải được dữ liệu nhập hàng");
      });
  }, []);

  const totalValue = useMemo(
    () =>
      draftLines.reduce((sum, line) => {
        const normalized = draftNormalized(line);
        return normalized ? sum + normalized.quantity * normalized.unitCost : sum;
      }, 0),
    [draftLines],
  );

  const canPreview =
    rows.length > 0 &&
    Boolean(mapping.quantity) &&
    Boolean(mapping.unitCost) &&
    Boolean(mapping.sku) &&
    (paymentMethod !== "DEBT" || Boolean(supplierId));

  async function chooseFile(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    setBusy(true);
    setError("");
    setMessage("");
    setJobId("");
    setSummary(null);
    setDraftLines([]);
    setConfirmedReceiptId("");

    try {
      const lower = file.name.toLowerCase();
      if (file.size > 8 * 1024 * 1024) {
        throw new Error("Tệp nhập tối đa 8 MB.");
      }

      let nextRows: RawRow[];
      let nextHeaders: string[];
      let nextHash: string;
      let nextSource: SourceType;

      if (lower.endsWith(".csv")) {
        const parsed = parseCsv(await file.text());
        nextRows = parsed;
        nextHeaders = parsed[0] ? Object.keys(parsed[0]) : [];
        nextHash = await fileHash(file);
        nextSource = "CSV";
      } else if (lower.endsWith(".xlsx")) {
        if (file.size > 8 * 1024 * 1024) throw new Error("Tệp XLSX tối đa 8 MB.");

        const parsed = await api<{
          headers: string[];
          rows: RawRow[];
          rowCount: number;
          sourceHash: string;
        }>("/imports/parse-xlsx", {
          method: "POST",
          body: JSON.stringify({
            fileName: file.name,
            base64: toBase64(await file.arrayBuffer()),
          }),
        });

        nextRows = parsed.rows;
        nextHeaders = parsed.headers;
        nextHash = parsed.sourceHash;
        nextSource = "XLSX";
      } else {
        throw new Error("BLed chỉ nhận CSV hoặc XLSX cho luồng này.");
      }

      if (nextRows.length === 0) throw new Error("Tệp không có dòng dữ liệu.");
      if (nextRows.length > 500) {
        throw new Error("Mỗi lần nhập tối đa 500 dòng. Hãy tách tệp thành nhiều phần.");
      }

      if (looksLikeProductCatalog(nextHeaders, file.name)) {
        throw new Error(
          "Đây là file Danh mục sản phẩm/SKU, không phải Phiếu nhập hàng. Hãy dùng Sản phẩm → Thêm sản phẩm → Nhập từ file CSV/Excel. Sau khi SKU được tạo, quay lại Nhập hàng bằng file phiếu nhập có SKU + Số lượng + Giá nhập.",
        );
      }

      setFileName(file.name);
      setSourceType(nextSource);
      setSourceHash(nextHash);
      setHeaders(nextHeaders);
      setRows(nextRows);
      setMapping(autoMapping(nextHeaders));
      setMessage(
        `Đã đọc ${nextRows.length} dòng từ ${file.name}. Phiếu nhập chỉ sử dụng SKU đã tồn tại; hãy kiểm tra ánh xạ trước khi tạo bản nháp.`,
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : "Không đọc được tệp");
    } finally {
      setBusy(false);
    }
  }

  function patchRawRow(index: number, field: keyof Mapping, value: string) {
    const sourceColumn = mapping[field];
    if (!sourceColumn) return;
    setRows((current) =>
      current.map((row, rowIndex) =>
        rowIndex === index ? { ...row, [sourceColumn]: value } : row,
      ),
    );
  }

  function currentMetadata() {
    return {
      supplierId: supplierId || null,
      paymentMethod,
      note: note.trim() || null,
    };
  }

  async function createPreview() {
    if (!sourceType || !canPreview) return;
    setBusy(true);
    setError("");
    setMessage("");

    try {
      const result = await api<PreviewResponse>("/imports/preview", {
        method: "POST",
        body: JSON.stringify({
          kind: "GOODS_RECEIPT",
          source: sourceType,
          sourceFileName: fileName || null,
          sourceHash: sourceHash || null,
          mapping,
          metadata: currentMetadata(),
          rows,
        }),
      });

      setJobId(result.job.id);
      setSummary(result.summary);
      setDraftLines(result.job.lines);
      setMessage(
        result.summary.errors === 0
          ? "Bản nháp đã sẵn sàng. BLed chưa ghi kho; hãy kiểm tra bảng trước khi xác nhận."
          : "Bản nháp đã lưu nhưng còn lỗi. Sửa dữ liệu và bấm Kiểm tra lại.",
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : "Không tạo được bản nháp nhập liệu");
    } finally {
      setBusy(false);
    }
  }

  async function revalidate() {
    if (!jobId) return;
    setBusy(true);
    setError("");
    setMessage("");

    try {
      const result = await api<RevalidateResponse>(`/imports/${jobId}/revalidate`, {
        method: "POST",
        body: JSON.stringify({
          mapping,
          metadata: currentMetadata(),
          rows,
        }),
      });

      setSummary(result.summary);
      setDraftLines(result.lines);
      setMessage(
        result.summary.errors === 0
          ? "Dữ liệu đã được kiểm tra lại và sẵn sàng xác nhận."
          : "Vẫn còn lỗi cần xử lý trước khi nhập kho.",
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : "Không kiểm tra lại được dữ liệu");
    } finally {
      setBusy(false);
    }
  }

  async function confirmImport() {
    if (!jobId || !summary || summary.errors > 0) return;
    setBusy(true);
    setError("");
    setMessage("");

    try {
      const prepared = await api<{
        receiveStockPayload?: Record<string, unknown>;
        confirmedEntityId?: string | null;
      }>(`/imports/${jobId}/prepare`, {
        method: "POST",
      });

      if (prepared.confirmedEntityId) {
        setConfirmedReceiptId(prepared.confirmedEntityId);
        setMessage("Phiếu này đã được nhập kho trước đó.");
        return;
      }

      if (!prepared.receiveStockPayload) {
        throw new Error("BLed không tạo được lệnh nhập kho từ bản nháp.");
      }

      const receipt = await api<{ receiptId: string; total: number }>(
        "/inventory/receive",
        {
          method: "POST",
          body: JSON.stringify(prepared.receiveStockPayload),
        },
      );

      await api(`/imports/${jobId}/complete`, {
        method: "POST",
        body: JSON.stringify({ receiptId: receipt.receiptId }),
      });

      setConfirmedReceiptId(receipt.receiptId);
      await refreshFromServer();
      setMessage(
        `Đã nhập kho thành công ${summary.total} dòng. Phiếu nhập: ${receipt.receiptId}.`,
      );
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Không thể xác nhận nhập kho. Bản nháp vẫn được giữ nguyên.",
      );
    } finally {
      setBusy(false);
    }
  }

  function downloadTemplate() {
    const csv = toCsv(RECEIPT_TEMPLATE_HEADERS, [
      {
        SKU: "SP001",
        "Mã vạch": "",
        "Tên sản phẩm": "Tên hàng đã có trong BLed",
        "Đơn vị": "cái",
        "Số lượng": 10,
        "Giá nhập": 15000,
        "Mã lô": "",
        "Hạn sử dụng": "",
      },
    ]);

    downloadText(
      "bledger-phieu-nhap-template.csv",
      csv,
      "text/csv;charset=utf-8",
    );
  }

  function exportDraftSheet() {
    const sourceLines =
      draftLines.length > 0
        ? draftLines
        : rows.map(
            (raw, index) =>
              ({
                rowNumber: index + 2,
                raw,
                status: "DRAFT",
              }) as DraftLine,
          );

    const csvRows = sourceLines.map((line) => {
      const normalized = draftNormalized(line);
      const raw = draftRaw(line);
      return {
        "Dòng": line.rowNumber,
        SKU: normalized?.sku ?? (mapping.sku ? raw[mapping.sku] ?? "" : ""),
        "Tên sản phẩm":
          normalized?.productName ??
          (mapping.productName ? raw[mapping.productName] ?? "" : ""),
        "Đơn vị":
          normalized?.unitName ?? (mapping.unit ? raw[mapping.unit] ?? "" : ""),
        "Số lượng":
          normalized?.quantity ??
          (mapping.quantity ? raw[mapping.quantity] ?? "" : ""),
        "Giá nhập":
          normalized?.unitCost ??
          (mapping.unitCost ? raw[mapping.unitCost] ?? "" : ""),
        "Mã lô":
          normalized?.lotCode ??
          (mapping.lotCode ? raw[mapping.lotCode] ?? "" : ""),
        "Hạn sử dụng":
          normalized?.expiryDate ??
          (mapping.expiryDate ? raw[mapping.expiryDate] ?? "" : ""),
        "Trạng thái": line.status,
        "Lỗi / cảnh báo": [...draftErrors(line), ...draftWarnings(line)].join(
          " | ",
        ),
      };
    });

    downloadText(
      `bledger-phieu-nhap-draft-${new Date().toISOString().slice(0, 10)}.csv`,
      toCsv(
        [
          "Dòng",
          "SKU",
          "Tên sản phẩm",
          "Đơn vị",
          "Số lượng",
          "Giá nhập",
          "Mã lô",
          "Hạn sử dụng",
          "Trạng thái",
          "Lỗi / cảnh báo",
        ],
        csvRows,
      ),
      "text/csv;charset=utf-8",
    );
  }

  async function chooseImage(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || !ocr?.enabled) return;

    setBusy(true);
    setError("");

    try {
      const result = await api<{
        headers: string[];
        rows: RawRow[];
        sourceHash?: string;
      }>("/imports/ocr/extract", {
        method: "POST",
        body: JSON.stringify({
          fileName: file.name,
          mimeType: file.type,
          base64: toBase64(await file.arrayBuffer()),
        }),
      });

      setFileName(file.name);
      setSourceType("OCR");
      setHeaders(result.headers);
      setRows(result.rows);
      setSourceHash(result.sourceHash ?? (await fileHash(file)));
      setMapping(autoMapping(result.headers));
      setJobId("");
      setSummary(null);
      setDraftLines([]);
      setMessage(
        "Ảnh đã được trích xuất thành bảng nháp. Hãy kiểm tra từng dòng trước khi xác nhận.",
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : "Không đọc được ảnh");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="page-stack">
      <PageHeader
        back
        title="Nhập liệu thông minh"
        description="CSV/XLSX → bảng kiểm tra → xác nhận → phiếu nhập kho. Không có dữ liệu nào được ghi vào sổ kho trước bước xác nhận."
      />

      {message ? <InlineNotice tone="success" title={message} /> : null}
      {error ? <InlineNotice tone="danger" title={error} /> : null}

      <section>
        <div className="section-title">
          <div>
            <Upload size={20} />
            <h2>1. Chọn nguồn dữ liệu</h2>
          </div>
          <StatusBadge tone="info">An toàn theo bản nháp</StatusBadge>
        </div>

        <div className="smart-import-source-grid">
          <Card className="smart-import-source-card">
            <FileSpreadsheet size={28} />
            <strong>Nhập từ CSV / XLSX</strong>
            <p>
              Dùng bảng giá, phiếu nhập hoặc dữ liệu từ Excel/Google Sheets.
            </p>
            <div className="data-import-actions">
              <button
                className="btn btn-primary"
                disabled={busy}
                onClick={() => fileRef.current?.click()}
              >
                <Upload size={18} /> Chọn tệp
              </button>
              <button className="btn btn-secondary" onClick={downloadTemplate}>
                <Download size={18} /> Tải mẫu CSV
              </button>
            </div>
            <input
              ref={fileRef}
              hidden
              type="file"
              accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={(event) => void chooseFile(event)}
            />
          </Card>

          <Card className="smart-import-source-card">
            <Camera size={28} />
            <strong>Chụp phiếu nhập</strong>
            <p>
              Khung OCR đã tách khỏi ledger. Kết quả ảnh luôn qua bảng nháp và
              bắt buộc xác nhận.
            </p>
            <button
              className="btn btn-secondary"
              disabled={!ocr?.enabled || busy}
              onClick={() => imageRef.current?.click()}
            >
              <Camera size={18} />
              {ocr?.enabled ? "Chụp / chọn ảnh" : "OCR chưa bật"}
            </button>
            <input
              ref={imageRef}
              hidden
              type="file"
              accept="image/jpeg,image/png,image/webp"
              capture="environment"
              onChange={(event) => void chooseImage(event)}
            />
            <small>{ocr?.message ?? "Đang kiểm tra khả năng OCR..."}</small>
          </Card>
        </div>
      </section>

      {rows.length > 0 ? (
        <>
          <section>
            <div className="section-title">
              <div>
                <FileSpreadsheet size={20} />
                <h2>2. Ánh xạ cột</h2>
              </div>
              <StatusBadge tone="neutral">{rows.length} dòng</StatusBadge>
            </div>

            <Card className="form-stack">
              <div className="smart-import-file-meta">
                <div>
                  <strong>{fileName}</strong>
                  <small>
                    {sourceType} · SHA-256{" "}
                    {sourceHash ? `${sourceHash.slice(0, 12)}…` : "đang tính"}
                  </small>
                </div>
                <button
                  className="btn btn-secondary compact"
                  onClick={exportDraftSheet}
                >
                  <Download size={16} /> Xuất bảng tính CSV
                </button>
              </div>

              <InlineNotice tone="info" title="SKU là định danh bắt buộc">
                Nhập kho chỉ nhận SKU đã tồn tại trong Danh mục sản phẩm. Mã
                vạch và tên chỉ dùng để đối chiếu, không dùng để tạo hay đoán
                mặt hàng mới.
              </InlineNotice>

              <div className="smart-import-mapping-grid">
                {FIELD_LABELS.map((field) => (
                  <label key={field.key}>
                    {field.label}
                    {field.required ? " *" : ""}
                    <select
                      value={mapping[field.key] ?? ""}
                      onChange={(event) => {
                        const selected = event.target.value;
                        setMapping((current) => {
                          const next = { ...current } as Record<string, string | undefined>;
                          if (selected) {
                            for (const key of Object.keys(next)) {
                              if (key !== field.key && next[key] === selected) {
                                next[key] = undefined;
                              }
                            }
                          }
                          next[field.key] = selected || undefined;
                          return next as Mapping;
                        });
                      }}
                    >
                      <option value="">— Không dùng cột này —</option>
                      {headers.map((header) => (
                        <option key={header} value={header}>
                          {header}
                        </option>
                      ))}
                    </select>
                  </label>
                ))}
              </div>
            </Card>
          </section>

          <section>
            <div className="section-title">
              <div>
                <ShieldCheck size={20} />
                <h2>3. Thông tin phiếu nhập</h2>
              </div>
            </div>

            <Card className="form-stack">
              <div className="form-grid compact-grid">
                <label>
                  Nhà cung cấp
                  <select
                    value={supplierId}
                    onChange={(event) => setSupplierId(event.target.value)}
                  >
                    <option value="">Không chọn</option>
                    {suppliers.map((supplier) => (
                      <option key={supplier.id} value={supplier.id}>
                        {supplier.name}
                      </option>
                    ))}
                  </select>
                </label>

                <label>
                  Thanh toán
                  <select
                    value={paymentMethod}
                    onChange={(event) =>
                      setPaymentMethod(event.target.value as PaymentMethod)
                    }
                  >
                    <option value="CASH">Tiền mặt</option>
                    <option value="BANK_TRANSFER">Chuyển khoản</option>
                    <option value="DEBT">Ghi nợ nhà cung cấp</option>
                  </select>
                </label>
              </div>

              <label>
                Ghi chú
                <input
                  value={note}
                  maxLength={500}
                  placeholder="VD: Phiếu giao ngày 06/09"
                  onChange={(event) => setNote(event.target.value)}
                />
              </label>

              {paymentMethod === "DEBT" && !supplierId ? (
                <InlineNotice tone="warning" title="Ghi nợ cần nhà cung cấp">
                  Chọn nhà cung cấp trước khi tạo bản nháp để công nợ được ghi
                  đúng đối tượng.
                </InlineNotice>
              ) : null}

              {!jobId ? (
                <button
                  className="btn btn-primary btn-lg"
                  disabled={busy || !canPreview || !navigator.onLine}
                  onClick={() => void createPreview()}
                >
                  <ShieldCheck size={20} />
                  {busy ? "Đang kiểm tra..." : "Tạo bản nháp & kiểm tra dữ liệu"}
                </button>
              ) : (
                <button
                  className="btn btn-secondary"
                  disabled={busy || !navigator.onLine}
                  onClick={() => void revalidate()}
                >
                  <RefreshCw size={18} />
                  {busy ? "Đang kiểm tra..." : "Kiểm tra lại dữ liệu"}
                </button>
              )}
            </Card>
          </section>
        </>
      ) : null}

      {summary && draftLines.length > 0 ? (
        <section>
          <div className="section-title">
            <div>
              <CheckCircle2 size={20} />
              <h2>4. Kiểm tra trước khi nhập kho</h2>
            </div>
            <StatusBadge
              tone={
                summary.errors > 0
                  ? "danger"
                  : summary.warnings > 0
                    ? "warning"
                    : "success"
              }
            >
              {summary.errors > 0 ? "Cần sửa" : "Sẵn sàng"}
            </StatusBadge>
          </div>

          <div className="import-preview">
            <div>
              <strong>{summary.total}</strong>
              <span>Tổng dòng</span>
            </div>
            <div>
              <strong className="text-success">{summary.valid}</strong>
              <span>Hợp lệ</span>
            </div>
            <div>
              <strong
                className={
                  summary.errors
                    ? "text-danger"
                    : summary.warnings
                      ? "text-warning"
                      : "text-success"
                }
              >
                {summary.errors || summary.warnings}
              </strong>
              <span>{summary.errors ? "Lỗi" : "Cảnh báo"}</span>
            </div>
          </div>

          {summary.metadataErrors.length > 0 ? (
            <InlineNotice tone="danger" title="Thông tin phiếu chưa hợp lệ">
              {summary.metadataErrors.join(" · ")}
            </InlineNotice>
          ) : null}

          <Card className="smart-import-table-card">
            <div className="smart-import-table-wrap">
              <table className="smart-import-table">
                <thead>
                  <tr>
                    <th>Dòng</th>
                    <th>Sản phẩm</th>
                    <th>Đơn vị</th>
                    <th>Số lượng</th>
                    <th>Giá nhập</th>
                    <th>Lô</th>
                    <th>HSD</th>
                    <th>Trạng thái</th>
                  </tr>
                </thead>
                <tbody>
                  {draftLines.map((line, index) => {
                    const normalized = draftNormalized(line);
                    const errors = draftErrors(line);
                    const warnings = draftWarnings(line);
                    const raw = rows[index] ?? draftRaw(line);
                    const productSource = mapping.sku;

                    return (
                      <tr
                        key={`${line.rowNumber}-${index}`}
                        className={
                          line.status === "ERROR"
                            ? "row-error"
                            : line.status === "WARNING"
                              ? "row-warning"
                              : ""
                        }
                      >
                        <td>{line.rowNumber}</td>
                        <td>
                          {productSource ? (
                            <input
                              value={String(raw[productSource] ?? "")}
                              aria-label={`Sản phẩm dòng ${line.rowNumber}`}
                              onChange={(event) => {
                                patchRawRow(index, "sku", event.target.value);
                              }}
                            />
                          ) : null}
                          {normalized ? (
                            <small>
                              {normalized.productName} · {normalized.sku}
                            </small>
                          ) : null}
                        </td>
                        <td>
                          {mapping.unit ? (
                            <input
                              value={String(raw[mapping.unit] ?? "")}
                              onChange={(event) =>
                                patchRawRow(index, "unit", event.target.value)
                              }
                            />
                          ) : (
                            <span>{normalized?.unitName ?? "ĐVT cơ bản"}</span>
                          )}
                        </td>
                        <td>
                          <input
                            inputMode="decimal"
                            value={String(raw[mapping.quantity] ?? "")}
                            onChange={(event) =>
                              patchRawRow(index, "quantity", event.target.value)
                            }
                          />
                        </td>
                        <td>
                          <input
                            inputMode="numeric"
                            value={String(raw[mapping.unitCost] ?? "")}
                            onChange={(event) =>
                              patchRawRow(index, "unitCost", event.target.value)
                            }
                          />
                        </td>
                        <td>
                          {mapping.lotCode ? (
                            <input
                              value={String(raw[mapping.lotCode] ?? "")}
                              onChange={(event) =>
                                patchRawRow(index, "lotCode", event.target.value)
                              }
                            />
                          ) : (
                            "—"
                          )}
                        </td>
                        <td>
                          {mapping.expiryDate ? (
                            <input
                              placeholder="YYYY-MM-DD"
                              value={String(raw[mapping.expiryDate] ?? "")}
                              onChange={(event) =>
                                patchRawRow(index, "expiryDate", event.target.value)
                              }
                            />
                          ) : (
                            "—"
                          )}
                        </td>
                        <td>
                          <StatusBadge
                            tone={
                              line.status === "ERROR"
                                ? "danger"
                                : line.status === "WARNING"
                                  ? "warning"
                                  : "success"
                            }
                          >
                            {line.status === "ERROR"
                              ? "Lỗi"
                              : line.status === "WARNING"
                                ? "Cảnh báo"
                                : "Hợp lệ"}
                          </StatusBadge>
                          {[...errors, ...warnings].map((item) => (
                            <small
                              key={item}
                              className={
                                errors.includes(item)
                                  ? "text-danger"
                                  : "text-warning"
                              }
                            >
                              {item}
                            </small>
                          ))}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>

          <div className="sticky-summary">
            <div>
              <span>Tổng giá trị dự kiến</span>
              <Money value={totalValue} className="money-large" />
            </div>
            <div>
              <span>Thanh toán</span>
              <strong>{paymentLabel(paymentMethod)}</strong>
            </div>
            <button
              className="btn btn-primary btn-lg"
              disabled={
                busy ||
                Boolean(confirmedReceiptId) ||
                summary.errors > 0 ||
                !navigator.onLine
              }
              onClick={() => void confirmImport()}
            >
              <ShieldCheck size={20} />
              {confirmedReceiptId
                ? "Đã nhập kho"
                : busy
                  ? "Đang xác nhận..."
                  : `Xác nhận nhập kho ${summary.total} dòng`}
            </button>
          </div>
        </section>
      ) : null}

      <InlineNotice tone="info" title="Nguyên tắc an toàn của BLed">
        Tệp và OCR chỉ tạo bản nháp. Việc ghi tồn kho, chi tiền hoặc công nợ chỉ
        xảy ra qua nghiệp vụ Nhập hàng sau khi người dùng xác nhận. Nếu có lỗi
        mạng, idempotency key giúp tránh tạo trùng phiếu nhập.
      </InlineNotice>

      <Card className="smart-import-footer-card">
        <AlertTriangle size={20} />
        <div>
          <strong>SKU chưa có trong danh mục?</strong>
          <p>
            Nếu file có SKU, BLed sẽ hiển thị bước <strong>Tạo SKU chưa có</strong>
            ngay trong bảng kiểm tra. Hệ thống không tự đoán tên hoặc giá bán và
            không cộng tồn kho ở bước tạo mặt hàng. Nếu dòng không có SKU, hãy tạo
            sản phẩm tại{" "}
            <Link to="/products/new">
              <strong>Sản phẩm → Thêm sản phẩm</strong>
            </Link>
            rồi kiểm tra lại.
          </p>
        </div>
      </Card>
    </div>
  );
}
